import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import GroupsIcon from "@mui/icons-material/Groups";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import ApartmentIcon from "@mui/icons-material/Apartment";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { CuadrillaCard } from "./CuadrillaCard";
import { getDetalleParteGrupo, updateParteGrupoEstado } from "../../services/apis";
import { formatDateTimeEs } from "../../utils/formatters";

const actualizarAvatarEnDetalle = (detalle, idCuadrilla, idUsuario, imagenUrl) => {
	if (!detalle || detalle?.datos_cuadrilla?.id_cuadrilla !== idCuadrilla) {
		return detalle;
	}

	const actualizarGrupo = (grupo = []) =>
		grupo.map((recolector) =>
			recolector?.id_usuario === idUsuario
				? { ...recolector, imagen_url: imagenUrl }
				: recolector
		);

	return {
		...detalle,
		recolectores: {
			...detalle.recolectores,
			no_comprobados: actualizarGrupo(detalle.recolectores?.no_comprobados),
			comprobados: actualizarGrupo(detalle.recolectores?.comprobados),
			error_comprobacion: actualizarGrupo(detalle.recolectores?.error_comprobacion),
		},
	};
};

export const TablaCuadrillas = ({
	currentCuadrillas,
	setOpen,
	open,
	onRecolectorAvatarUploaded,
}) => {
	const dialogOpen = Number.isInteger(open);
	const [dialogCuadrillaId, setDialogCuadrillaId] = useState(null);
	const [detalleCuadrilla, setDetalleCuadrilla] = useState(null);
	const [detalleLoading, setDetalleLoading] = useState(false);
	const [detalleError, setDetalleError] = useState("");

	const selectedCuadrilla = useMemo(() => {
		if (!dialogOpen) return null;
		return currentCuadrillas?.[open] || null;
	}, [currentCuadrillas, dialogOpen, open]);

	useEffect(() => {
		const nextCuadrillaId = selectedCuadrilla?.datos_cuadrilla?.id_cuadrilla ?? null;

		if (dialogOpen && nextCuadrillaId) {
			setDialogCuadrillaId(nextCuadrillaId);
		}
	}, [dialogOpen, selectedCuadrilla?.datos_cuadrilla?.id_cuadrilla]);

	useEffect(() => {
		let active = true;
		const idCuadrilla = dialogCuadrillaId;

		if (!dialogOpen || !idCuadrilla) {
			return () => {
				active = false;
			};
		}

		const cargarDetalle = async () => {
			setDetalleLoading(true);
			setDetalleError("");

			try {
				const response = await getDetalleParteGrupo(idCuadrilla);

				if (!active) return;

				if (response?.success) {
					setDetalleCuadrilla(response.data || null);
				} else {
					setDetalleCuadrilla(null);
					setDetalleError(response?.message || "No se pudo cargar el detalle del grupo");
				}
			} catch (error) {
				console.error("Error al cargar detalle de grupo:", error);
				if (active) {
					setDetalleCuadrilla(null);
					setDetalleError("No se pudo cargar el detalle del grupo");
				}
			} finally {
				if (active) {
					setDetalleLoading(false);
				}
			}
		};

		cargarDetalle();

		return () => {
			active = false;
		};
	}, [dialogCuadrillaId, dialogOpen]);

	const cerrarDetalle = () => {
		setOpen(null);
	};

	const handleEstadoChange = async ({ idCuadrilla, field, value }) => {
		try {
			await updateParteGrupoEstado({
				id_cuadrilla: idCuadrilla,
				[field]: Number(value),
			});
		} catch (error) {
			console.error("Error al actualizar el estado del parte de grupo:", error);
		}
	};

	const handleDetalleExited = () => {
		setDetalleCuadrilla(null);
		setDetalleLoading(false);
		setDetalleError("");
		setDialogCuadrillaId(null);
	};

	const refetchDetalleCuadrilla = async () => {
		const idCuadrilla =
			detalleCuadrilla?.datos_cuadrilla?.id_cuadrilla ||
			selectedCuadrilla?.datos_cuadrilla?.id_cuadrilla ||
			null;

		if (!idCuadrilla) return;

		setDetalleLoading(true);
		setDetalleError("");

		try {
			const response = await getDetalleParteGrupo(idCuadrilla);

			if (response?.success) {
				setDetalleCuadrilla(response.data || null);
			} else {
				setDetalleCuadrilla(null);
				setDetalleError(response?.message || "No se pudo cargar el detalle del grupo");
			}
		} catch (error) {
			console.error("Error al recargar detalle de grupo:", error);
			setDetalleCuadrilla(null);
			setDetalleError("No se pudo cargar el detalle del grupo");
		} finally {
			setDetalleLoading(false);
		}
	};

	const handleRecolectorAvatarUploadedLocal = (payload) => {
		if (typeof onRecolectorAvatarUploaded === "function") {
			onRecolectorAvatarUploaded(payload);
		}

		setDetalleCuadrilla((current) =>
			actualizarAvatarEnDetalle(
				current,
				payload.idCuadrilla,
				payload.idUsuario,
				payload.imagenUrl
			)
		);
	};

	const tituloDetalle =
		detalleCuadrilla?.datos_cuadrilla?.id_cuadrilla ||
		dialogCuadrillaId ||
		"";

	return (
		<>
			<Table stickyHeader sx={{ minWidth: "100%" }} size="small">
				<TableHead>
					<TableRow>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<GroupWorkIcon sx={{ mb: -1, fontSize: 30 }} color="inherit" /> ID
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<GroupWorkIcon sx={{ mb: -1, fontSize: 30 }} color="warning" /> N. Grupo
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<EventIcon sx={{ mb: -1, fontSize: 30 }} color="primary" /> Fecha parte de grupo
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<ApartmentIcon sx={{ mb: -1, fontSize: 30 }} color="success" /> Empresa
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<AssignmentIndIcon sx={{ mb: -1, fontSize: 30 }} color="secondary" /> Cabo
						</TableCell>

						<TableCell align="left" sx={{ fontSize: 20 }}>
							Validado
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							Revisado
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<GroupsIcon sx={{ mb: -1, fontSize: 30 }} color="inherit" /> Personas
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<GroupsIcon sx={{ mb: -1, fontSize: 30 }} color="secondary" /> Fichados
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<GroupsIcon sx={{ mb: -1, fontSize: 30 }} color="warning" /> Pendientes
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<GroupsIcon sx={{ mb: -1, fontSize: 30 }} color="error" /> No reconocidos
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					{currentCuadrillas.map((info, index) => {
						const totalRecolectores =
							info.recolectores.no_comprobados.length +
							info.recolectores.comprobados.length +
							info.recolectores.error_comprobacion.length;

						const comprobados = info.recolectores.comprobados.length;
						const noComprobados = info.recolectores.no_comprobados.length;
						const errorComprobacion = info.recolectores.error_comprobacion.length;
						const intentosNoPertenece = Number(
							info?.datos_cuadrilla?.intentos_no_pertenece ?? 0
						);
						const intentosNoRegistrado = Number(
							info?.datos_cuadrilla?.intentos_no_registrado ?? 0
						);
						const noReconocidos =
							errorComprobacion +
							(Number.isFinite(intentosNoPertenece) ? intentosNoPertenece : 0) +
							(Number.isFinite(intentosNoRegistrado) ? intentosNoRegistrado : 0);
						const idCuadrilla = info?.datos_cuadrilla?.id_cuadrilla ?? index;

						return (
							<TableRow key={idCuadrilla}>
								<TableCell align="center" sx={{ fontSize: 17 }}>
									{info.datos_cuadrilla.id_cuadrilla}
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 17 }}>
									{info.datos_cuadrilla.nombre_cuadrilla}
								</TableCell>
								<TableCell align="left" sx={{ fontSize: 17 }}>
									{formatDateTimeEs(info.datos_cuadrilla.fecha_cuadrilla)}
								</TableCell>

								<TableCell align="left" sx={{ fontSize: 17 }}>
									{info.datos_cuadrilla.nombre_cliente}
								</TableCell>
								<TableCell align="left" sx={{ fontSize: 17 }}>
									{info.datos_cuadrilla.apellidos_cabo} 	{info.datos_cuadrilla.nombre_cabo}
								</TableCell>
								<TableCell align="left" sx={{ fontSize: 17 }}>
									<FormControl variant="standard" fullWidth size="small">
										<InputLabel id={`cuadrilla-validado-${idCuadrilla}`}>Validado</InputLabel>
										<Select
											labelId={`cuadrilla-validado-${idCuadrilla}`}
											id={`cuadrilla-validado-select-${idCuadrilla}`}
											label="Validado"
											defaultValue={info.datos_cuadrilla.validado}
											onChange={(event) =>
												handleEstadoChange({
													idCuadrilla,
													field: "validado",
													value: event.target.value,
												})
											}
										>
											<MenuItem value={0}>No</MenuItem>
											<MenuItem value={1}>Si</MenuItem>
										</Select>
									</FormControl>
								</TableCell>
								<TableCell align="left" sx={{ fontSize: 17 }}>
									<FormControl variant="standard" fullWidth size="small">
										<InputLabel id={`cuadrilla-revisado-${idCuadrilla}`}>Revisado</InputLabel>
										<Select
											labelId={`cuadrilla-revisado-${idCuadrilla}`}
											id={`cuadrilla-revisado-select-${idCuadrilla}`}
											label="Revisado"
											defaultValue={info.datos_cuadrilla.revisado}
											onChange={(event) =>
												handleEstadoChange({
													idCuadrilla,
													field: "revisado",
													value: event.target.value,
												})
											}
										>
											<MenuItem value={0}>No</MenuItem>
											<MenuItem value={1}>Si</MenuItem>
										</Select>
									</FormControl>
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 17 }}>
									<Button variant="text" onClick={() => setOpen(index)}>
										{totalRecolectores}
									</Button>
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 17 }}>
									<Button variant="text" onClick={() => setOpen(index)}>
										{comprobados}
									</Button>
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 17 }}>
									<Button variant="text" onClick={() => setOpen(index)}>
										{noComprobados}
									</Button>
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 17 }}>
									<Button variant="text" onClick={() => setOpen(index)}>
										{noReconocidos}
									</Button>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>

			<Dialog
				open={dialogOpen}
				onClose={cerrarDetalle}
				aria-labelledby="detalle-parte-grupo-title"
				fullWidth
				maxWidth="xl"
				keepMounted
				TransitionProps={{ onExited: handleDetalleExited }}
			>
				<DialogTitle id="detalle-parte-grupo-title" component="div">
					<Grid
						container
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						size={{ xs: 12 }}
					>
						<Typography variant="h4">
							Informacion del grupo {tituloDetalle ? `#${tituloDetalle}` : ""}
						</Typography>
						<IconButton size="medium" onClick={cerrarDetalle}>
							<CloseIcon sx={{ fontSize: 30 }} />
						</IconButton>
					</Grid>
				</DialogTitle>

				<DialogContent>
					{detalleLoading && (
						<Box
							sx={{
								minHeight: "35vh",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<CircularProgress />
						</Box>
					)}

					{!detalleLoading && !!detalleError && <Alert severity="error">{detalleError}</Alert>}

					{!detalleLoading && !detalleError && detalleCuadrilla && (
						<CuadrillaCard
							info={detalleCuadrilla}
							parentDialogOpen={dialogOpen}
							onRecolectorAvatarUploaded={handleRecolectorAvatarUploadedLocal}
							onRefetchDetalle={refetchDetalleCuadrilla}
						/>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
};
